import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, fileUrl, formatNumber, trackEvent, getCampaignSource } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { browserLang, langName } from "@/lib/lang";
import { useSeoTags } from "@/lib/seo";
import { Globe2, Sparkles } from "lucide-react";
import AnimatedCounter from "@/components/AnimatedCounter";
import VoiceModal from "@/components/VoiceModal";
import PassItOn from "@/components/PassItOn";
import ClaimModal from "@/components/ClaimModal";
import ReportModal from "@/components/ReportModal";
import CreatorAnalytics from "@/components/CreatorAnalytics";

export default function MessagePage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const { user, requireAuth } = useAuth();
  const [data, setData] = useState(null);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [passOpen, setPassOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [viewerLang] = useState(() => browserLang());
  const navigate = useNavigate();

  const load = async (opts = {}) => {
    const lang = opts.original ? "" : viewerLang;
    const q = lang ? `?lang=${encodeURIComponent(lang)}` : "";
    // Reuse the boot-time metadata prefetch (see public/index.html) so
    // we don't fire a duplicate /api/messages/:id request on first
    // render. The boot script fetches with the same lang the initial
    // render would use, so on the happy path this awaits the SAME
    // in-flight promise the browser already started before the React
    // bundle downloaded. Any mismatch (translation toggle → opts.original)
    // or boot-fetch failure falls through to the normal fetch below.
    try {
      const boot = typeof window !== "undefined" ? window.__VOIHU_MSG_BOOT__ : null;
      if (boot && boot.id === id && boot.lang === (lang || "")) {
        window.__VOIHU_MSG_BOOT__ = null;
        const payload = await boot.promise;
        if (payload && payload.message) { setData(payload); return; }
      }
    } catch (_) { /* fall through to normal fetch */ }
    const r = await api.get(`/messages/${id}${q}`);
    setData(r.data);
  };

  useEffect(() => {
    load({ original: showOriginal });
    const source = getCampaignSource();
    api.post(`/messages/${id}/reach`, { ref, source }).catch(() => {});
    // OPEN/SCAN fires once per campaign entry to the MESSAGE (only when a
    // source is attributed); VIEW fires on every read.
    if (source) trackEvent(id, "open", { ref, source });
    trackEvent(id, "view", { ref, source });
    // eslint-disable-next-line
  }, [id, showOriginal]);

  // Per-MESSAGE SEO (title / description / canonical / OG / Twitter). Uses
  // the MESSAGE's own title + text — no keyword stuffing.
  //
  // IMPORTANT — Emergent edge-renderer timing:
  //   Social crawlers on production hit /message/:id, and Emergent's edge
  //   opens the app in a headless browser, waits for the render, then
  //   snapshots. On the FIRST render `data` is still `null` because the
  //   `/api/messages/:id` fetch has not resolved yet. If we called
  //   `useSeoTags(...)` with a partially-populated object at that point,
  //   it would commit `og:type=article` alongside the site-wide default
  //   `og:title` — exactly the misleading state Support observed. By
  //   passing `null` until `data.message` is present, `useSeoTags` skips
  //   any <head> mutation on that first render, leaving the correct
  //   VOIHU site-wide fallback tags from `index.html` in place until
  //   real MESSAGE data arrives — at which point every OG / Twitter /
  //   canonical / title / description tag is committed atomically.
  const seoTitle = data?.message?.title
    ? `${data.message.title} — VOIHU`
    : null;
  const seoDescription = data?.message?.text || null;
  const seoOgImage = data?.message?.photo_url
    ? fileUrl(data.message.photo_url)
    : null;
  useSeoTags(
    data?.message
      ? {
          title: seoTitle,
          description: seoDescription,
          canonical: `https://voihuworld.com/message/${id}`,
          ogImage: seoOgImage || undefined,
          ogType: "article",
        }
      : null,
    [id, data?.message?.id, seoTitle, seoDescription, seoOgImage],
  );

  if (!data) return <div className="max-w-[900px] mx-auto px-6 py-24 oh-label">Loading…</div>;
  const m = data.message;
  const heardBadge = m.status === "HEARD";
  const onAddVoice = () => { requireAuth(() => setVoiceOpen(true)); };
  const confirmVoice = async () => {
    await api.post(`/messages/${id}/voices`, { ref, source: getCampaignSource() });
    await load({ original: showOriginal });
  };

  const adminMarkHeard = async () => {
    if (!window.confirm("Mark this MESSAGE as HEARD? All voices will be notified.")) return;
    try {
      await api.post(`/admin/messages/${id}/heard`);
      await load({ original: showOriginal });
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to mark HEARD.");
    }
  };

  const canShowToggle = viewerLang && m.original_language && viewerLang !== m.original_language;

  return (
    <div className="max-w-[1100px] mx-auto px-6 sm:px-10 pt-14 pb-24">
      <Link to="/explore" className="oh-label hover:text-[color:var(--oh-red)]" data-testid="msg-back">
        ← Explore
      </Link>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-3 oh-label flex-wrap">
            <span>{m.destination_label || (m.destination_type === "everyone" ? "For everyone" : m.destination_type)}</span>
            <span>·</span>
            <span>{m.kind === "event" ? "Event" : "Permanent"}</span>
            {heardBadge && (<><span>·</span>
              <span className="text-[color:var(--oh-red)] flex items-center gap-1.5" data-testid="msg-heard">
                Heard ✓
                <span className="oh-label text-[color:var(--oh-red)]" data-testid="msg-heard-verified">
                  · Verified{m.verified_by_public_name ? ` by ${m.verified_by_public_name}` : ""}
                </span>
              </span></>)}
            {m.is_translated && (
              <>
                <span>·</span>
                <span className="text-[color:var(--oh-ink-3)]" data-testid="msg-translated-badge">
                  Translated from {langName(m.original_language)}
                </span>
              </>
            )}
          </div>
          <h1 className="font-serif-editorial text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mt-6" data-testid="msg-title">
            {m.title}
          </h1>
          <p className="mt-8 font-serif-editorial text-2xl sm:text-3xl leading-snug text-[color:var(--oh-ink)]" data-testid="msg-text">
            “{m.text}”
          </p>

          {canShowToggle && (
            <button onClick={() => setShowOriginal((v) => !v)}
                    className="mt-6 oh-label hover:text-[color:var(--oh-red)] transition-colors"
                    data-testid="msg-toggle-original">
              {showOriginal ? "View translated" : "View original"}
              {!showOriginal && m.original_language ? ` · ${langName(m.original_language)}` : ""}
            </button>
          )}

          {m.photo_url && (
            <div className="mt-10 aspect-[16/9] overflow-hidden bg-[color:var(--oh-bg-2)] relative">
              <img
                src={fileUrl(m.photo_url)} alt=""
                className="absolute top-1/2 left-1/2 pointer-events-none"
                style={m.photo_position ? {
                  transform: `translate(-50%, -50%) translate(${m.photo_position.x}px, ${m.photo_position.y}px) scale(${m.photo_position.scale})`,
                  width: "100%", height: "100%", objectFit: "cover",
                } : {
                  transform: "translate(-50%, -50%)",
                  width: "100%", height: "100%", objectFit: "cover",
                }}
              />
            </div>
          )}
          <div className="mt-12 oh-label" data-testid="msg-origin">
            Started by {m.creator_display_name} · {m.origin_city ? `${m.origin_city}, ` : ""}{m.origin_country}
          </div>

          {heardBadge && (
            <div className="mt-10 border-t oh-hairline pt-8" data-testid="msg-heard-block">
              <div className="oh-label text-[color:var(--oh-red)] flex items-center gap-2 flex-wrap">
                <span>Heard ✓</span>
                <span>·</span>
                <span data-testid="msg-heard-verified-block">
                  Verified{m.verified_by_public_name ? ` by ${m.verified_by_public_name}` : ""}
                </span>
              </div>
              <div className="font-serif-editorial text-3xl mt-3 leading-tight">
                They heard us. Now show how many of us mean it.
              </div>
              {m.heard_at && (
                <div className="oh-label mt-3 text-[color:var(--oh-ink-3)]">
                  Heard on {new Date(m.heard_at).toLocaleDateString(undefined,
                    { year: "numeric", month: "long", day: "numeric" })}
                </div>
              )}
              <button onClick={() => requireAuth(() => setPassOpen(true))}
                      className="btn-primary mt-6" data-testid="msg-heard-pass">
                Pass it on
              </button>
            </div>
          )}

          {!heardBadge && m.destination_type !== "everyone" && (
            <div className="mt-8 flex flex-wrap items-center gap-6">
              <button onClick={() => setClaimOpen(true)}
                      className="oh-label hover:text-[color:var(--oh-red)] transition-colors"
                      data-testid="msg-claim">
                Is this message for you? Claim this message →
              </button>
              {user?.is_admin && (
                <button onClick={adminMarkHeard}
                        className="oh-label text-[color:var(--oh-ink-3)] hover:text-[color:var(--oh-red)] transition-colors"
                        data-testid="msg-admin-heard">
                  Admin · mark HEARD →
                </button>
              )}
            </div>
          )}
          {!heardBadge && m.destination_type === "everyone" && user?.is_admin && (
            <div className="mt-8">
              <button onClick={adminMarkHeard}
                      className="oh-label text-[color:var(--oh-ink-3)] hover:text-[color:var(--oh-red)] transition-colors"
                      data-testid="msg-admin-heard">
                Admin · mark HEARD →
              </button>
            </div>
          )}
        </div>

        <aside className="lg:col-span-4 lg:sticky lg:top-24">
          <div className="oh-label">Voices</div>
          <div className="text-6xl sm:text-7xl font-light leading-none mt-2 tabular" data-testid="msg-voices">
            <AnimatedCounter value={m.voices_count} />
          </div>
          <div className="oh-label mt-3">
            {formatNumber(m.countries_count)} countries · {formatNumber(m.reached_count)} reached
          </div>

          <div className="mt-8 space-y-3">
            {data.has_voice ? (
              <button disabled className="btn-red w-full opacity-70 cursor-default" data-testid="msg-already-voiced">
                Your voice is in ✓
              </button>
            ) : (
              <button onClick={onAddVoice} className="btn-red w-full" data-testid="msg-add-voice">
                {m.is_founding ? "Add your voice · FREE" : "Add your voice · €1"}
              </button>
            )}
            <button onClick={() => requireAuth(() => setPassOpen(true))}
                    className="btn-ghost w-full" data-testid="msg-pass">
              Pass it on
            </button>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
              <button onClick={() => navigate(`/message/${id}/global-impact`)}
                      className="btn-ghost w-full flex items-center justify-center gap-2"
                      data-testid="msg-global-impact">
                <Globe2 className="w-4 h-4" aria-hidden="true" />
                See this MESSAGE travel
              </button>
              <button onClick={() => requireAuth(() => navigate(`/message/${id}/impact`))}
                      className="btn-ghost w-full flex items-center justify-center gap-2"
                      data-testid="msg-impact">
                <Sparkles className="w-4 h-4" aria-hidden="true" />
                See my impact
              </button>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t oh-hairline oh-label">Human depth · {m.human_depth}</div>
          <button onClick={() => requireAuth(() => setReportOpen(true))}
                  className="mt-4 oh-label text-[color:var(--oh-ink-3)] hover:text-[color:var(--oh-red)] transition-colors"
                  data-testid="msg-report">
            Report MESSAGE →
          </button>
        </aside>
      </div>

      <CreatorAnalytics messageId={m.id} enabled={!!data.can_see_analytics} />

      {Array.isArray(data.voice_updates) && data.voice_updates.length > 0 && (
        <section className="border-t oh-hairline" data-testid="voice-updates">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16">
            <div className="oh-label">Voice updates</div>
            <h2 className="font-serif-editorial text-2xl sm:text-3xl leading-[1.1] mt-3 tracking-tight">
              What happened next
            </h2>
            <ol className="mt-8 border-t oh-hairline">
              {data.voice_updates.map((ev) => {
                const when = ev.published_at || ev.created_at;
                const date = when
                  ? new Date(when).toLocaleDateString(undefined,
                      { month: "short", day: "numeric", year: "numeric" })
                  : "";
                let headline = ev.title || "";
                if (ev.event_type === "MILESTONE" && ev.threshold) {
                  headline = `${ev.threshold.toLocaleString("en-US")} Voices reached`;
                } else if (ev.event_type === "ACTION_SENT" && ev.recipient_name) {
                  headline = `Message brought to ${ev.recipient_name}`;
                } else if (ev.event_type === "RESPONSE_RECEIVED" && ev.recipient_name) {
                  headline = `${ev.recipient_name} responded`;
                }
                return (
                  <li key={ev.id}
                      className="grid grid-cols-[110px_1fr] gap-4 py-5 border-b oh-hairline items-baseline"
                      data-testid={`voice-update-${ev.event_type.toLowerCase()}`}>
                    <div className="oh-label text-[color:var(--oh-ink-3)]"
                         style={{ fontFamily: "Manrope, ui-sans-serif, system-ui, -apple-system, sans-serif",
                                  fontVariantNumeric: "lining-nums tabular-nums" }}>
                      {date}
                    </div>
                    <div>
                      <div className="font-serif-editorial text-lg sm:text-xl leading-[1.25] text-[color:var(--oh-ink)]">
                        {headline}
                      </div>
                      {ev.description ? (
                        <p className="mt-1 text-sm leading-[1.6] text-[color:var(--oh-ink-2)] max-w-[720px]">
                          {ev.description}
                        </p>
                      ) : null}
                      {ev.external_url ? (
                        <a href={ev.external_url}
                           target="_blank" rel="noopener noreferrer"
                           className="mt-1 inline-block text-sm underline text-[color:var(--oh-red)]"
                           data-testid={`voice-update-link-${ev.id}`}>
                          Read more →
                        </a>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </section>
      )}

      <VoiceModal open={voiceOpen} onOpenChange={setVoiceOpen}
                  onConfirm={() => load({ original: showOriginal })}
                  onPassItOn={() => { setVoiceOpen(false); setPassOpen(true); }}
                  messageTitle={m.title} messageId={m.id}
                  isFounding={!!m.is_founding}
                  referralRef={ref} />
      <PassItOn open={passOpen} onOpenChange={setPassOpen} message={m}
                incomingRef={ref} />
      <ClaimModal open={claimOpen} onOpenChange={setClaimOpen} message={m}
                  onHeard={() => load({ original: showOriginal })} />
      <ReportModal open={reportOpen} onOpenChange={setReportOpen} messageId={m.id} />
    </div>
  );
}
